import { Stack, SxProps, Tooltip, Typography } from "@mui/material";
import { ReactElement, ReactNode } from "react";
import { InfoIcon } from "src/foundations/icons";

export type SenecInfoTooltipProps = {
    /** Content can be either string or ReactNode */
    tooltipContent: ReactNode;
    /** Assigned to the tooltip content as key and data-testid (only works if the content is a string, not with ReactNodes) */
    tooltipId?: string;
    /** Use a specific color for the icon which triggers the snackbar, default is: "senecSecondary.neutralGray70" */
    triggerIconColor?: string;
    /** Assigned to the icon which triggers the tooltip as key and data-testid */
    triggerId?: string;
    /** Custom styles applied to the container of the icon which triggers the tooltip */
    triggerIconContainerSx?: SxProps;
    /** Assigned to the tooltip icon. */
    "data-testid"?: string;
};

export const SenecInfoTooltip = ({
    tooltipContent,
    tooltipId,
    triggerIconColor,
    triggerId,
    triggerIconContainerSx,
    ...otherProps
}: SenecInfoTooltipProps): ReactElement => {
    const tooltipContentIsString = typeof tooltipContent === "string";

    return (
        <Tooltip
            data-testid={otherProps["data-testid"]}
            title={
                tooltipContentIsString ? (
                    <Typography
                        variant="body1"
                        id={tooltipId}
                        data-testid={tooltipId}
                        key={tooltipId}
                        sx={{ whiteSpace: "pre-wrap" }}
                    >
                        {tooltipContent}
                    </Typography>
                ) : (
                    tooltipContent
                )
            }
            disableInteractive
            enterTouchDelay={0}
            leaveTouchDelay={1}
            enterNextDelay={100}
        >
            <Stack
                direction={"row"}
                alignItems={"center"}
                color={triggerIconColor ?? "senecSecondary.neutralGray70"}
                key={triggerId && `tooltip-trigger-container-${triggerId}`}
                sx={triggerIconContainerSx}
            >
                <InfoIcon
                    key={triggerId && `tooltip-trigger-icon${triggerId}`}
                    data-testid={triggerId}
                    id={triggerId}
                    sx={{
                        "&:hover": { cursor: "pointer" },
                    }}
                />
            </Stack>
        </Tooltip>
    );
};
